import { CURRENCIES } from '@pcrm/shared/money';
import { DEFAULT_MONTHS, expireDateFor, monthsBetween, today } from '@pcrm/shared/period';
import type { Package } from '../settings/packages.api';
import type { SubscriptionInput, SubscriptionListItem } from './subscriptions.api';

/**
 * Regulile de alegere a pachetului, scoase din ecran ca să se poată testa.
 *
 * Ce apără: **prepopularea din catalog nu are voie să calce peste ce a tastat operatorul.**
 * Un preț negociat, scris și apoi înlocuit tăcut de prețul de listă pentru că cineva a umblat
 * la filtrul de tip, e o pierdere de date care se vede abia la facturare. Regula stă aici,
 * într-un singur loc, nu împrăștiată în trei handlere de `onChange`.
 *
 * Vezi docs/wiki/domains/service-catalog.md#prețul-de-listă-vs-prețul-negociat
 */

/**
 * „Toate tipurile" — filtrul care nu filtrează.
 *
 * Nu e `''`: `Select` (Radix) refuză șirul gol ca valoare de opțiune. Un cuvânt românesc,
 * nu un UUID, deci nu se poate ciocni cu un tip real din catalog.
 */
export const ALL_TYPES = 'toate';

export interface PackageTypeOption {
  readonly id: string;
  readonly name: string;
  /**
   * Iconul tipului, luat de pe **primul** pachet întâlnit cu tipul ăsta.
   *
   * Toate pachetele unui tip îl poartă pe același, rezolvat în SQL din `crm_package_types`,
   * deci „primul" nu e o alegere între valori diferite — e doar cel care ajunge primul.
   */
  readonly icon: string;
}

/**
 * Tipurile care chiar au pachete în listă, alfabetic.
 *
 * Derivate din pachete, nu cerute separat de la `/catalog`: un tip fără niciun pachet de
 * ales ar fi o opțiune care duce la o listă goală. Alfabetic după regulile românești —
 * `sort()` implicit pune „Ș" după „Z".
 */
export function packageTypes(packages: readonly Package[]): PackageTypeOption[] {
  const byId = new Map<string, PackageTypeOption>();
  for (const item of packages) {
    if (!byId.has(item.typeId)) {
      byId.set(item.typeId, { id: item.typeId, name: item.typeName, icon: item.typeIcon });
    }
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, 'ro'));
}

/** Pachetele rămase după filtrul de tip. `ALL_TYPES` le lasă pe toate. */
export function packagesOfType(packages: readonly Package[], typeId: string): Package[] {
  return packages.filter((item) => typeId === ALL_TYPES || item.typeId === typeId);
}

/**
 * Ce se schimbă în formular când operatorul alege un pachet.
 *
 * `prefilled` = valorile de acum sunt ale catalogului, nu ale lui. Cât e adevărat, pachetul
 * ales aduce denumirea, prețul, moneda și convenția de TVA. Cum a scris el o valoare, aduce
 * doar `packageId`.
 */
export function applyPackage(
  form: SubscriptionInput,
  chosen: Package,
  prefilled: boolean,
): SubscriptionInput {
  if (!prefilled) {
    return { ...form, packageId: chosen.id };
  }

  return {
    ...form,
    packageId: chosen.id,
    name: chosen.name,
    cost: chosen.costDefault,
    currency: chosen.currency,
    // Convenția de TVA vine odată cu prețul: despărțite, s-ar putea prelua suma din catalog
    // și lăsa în urmă ce înseamnă ea.
    priceIncludesVat: chosen.priceIncludesVat,
  };
}

/**
 * Ce se schimbă în formular când operatorul mută filtrul de tip.
 *
 * **Doar pachetul, și numai dacă a ieșit din filtru.** Filtrul e o unealtă de căutare, nu o
 * schimbare de abonament: nu atinge denumirea, prețul, moneda sau convenția de TVA, și nu
 * repune `prefilled` pe `true`. Altfel, un preț negociat tastat s-ar întoarce la cel de
 * listă la următorul pachet ales.
 */
export function applyTypeFilter(
  form: SubscriptionInput,
  packages: readonly Package[],
  typeId: string,
): SubscriptionInput {
  const stillVisible = packagesOfType(packages, typeId).some((item) => item.id === form.packageId);
  return stillVisible ? form : { ...form, packageId: '' };
}

/**
 * Data care **se va scrie** la salvare, calculată cu aceeași funcție ca pe server.
 *
 * `null` = nu se poate calcula nimic: fără dată de început sau cu o durată care nu e un
 * întreg ≥ 1. Atunci formularul nu arată nicio dată, în loc să arate una inventată.
 */
export function plannedExpireDate(form: SubscriptionInput): string | null {
  if (form.startDate.length === 0 || !/^\d+$/.test(form.months.trim())) {
    return null;
  }

  return expireDateFor(form.startDate, Number(form.months));
}

/**
 * Ce se schimbă la salvare față de ce scrie în bază — sau `null`, dacă nimic.
 *
 * Rândurile vechi (seed, import din sistemul actual) pot avea o expirare care **nu** e ultima
 * zi a lunii, sau niciuna. Deschiderea formularului nu le atinge; el spune doar ce se va
 * întâmpla la salvare. **Nicio migrație nu normalizează rândurile existente:** ar muta
 * termene de pe contracte semnate, în masă și fără martor.
 *
 * `stored: null` e cazul separat — abonamentul n-avea termen, iar salvarea îi pune unul.
 * Confundat cu „nimic de anunțat", ar fi exact schimbarea tăcută pe care textul o previne.
 */
export function expireDateChange(
  form: SubscriptionInput,
  subscription: SubscriptionListItem | null,
): { readonly stored: string | null; readonly planned: string | null } | null {
  if (!subscription) {
    return null;
  }

  const planned = plannedExpireDate(form);

  return subscription.expireDate === planned ? null : { stored: subscription.expireDate, planned };
}

/**
 * Un abonament salvat → formular.
 *
 * Durata se **deduce** din lunile dintre început și expirare; unde nu se poate deduce (rând
 * fără expirare, sau cu una mai devreme de o lună), se cade pe valoarea implicită — iar
 * `movedExpireDate` se ocupă să spună că salvarea schimbă ceva.
 */
export function toInput(subscription: SubscriptionListItem): SubscriptionInput {
  const startDate = subscription.startDate ?? '';
  const months =
    startDate.length > 0 && subscription.expireDate
      ? monthsBetween(startDate, subscription.expireDate)
      : null;

  return {
    packageId: subscription.packageId,
    address: subscription.address ?? '',
    name: subscription.name,
    cost: subscription.cost,
    currency: subscription.currency,
    priceIncludesVat: subscription.priceIncludesVat,
    startDate,
    months: String(months ?? DEFAULT_MONTHS),
  };
}

/**
 * Formularul gol, cum se deschide pe „Adaugă un abonament".
 *
 * **Începe azi**, pentru că așa se introduce un contract în ziua în care se semnează — dar
 * rămâne editabil: nu toate contractele încep în ziua în care sunt introduse.
 */
export function empty(now: Date = new Date()): SubscriptionInput {
  return {
    packageId: '',
    address: '',
    name: '',
    cost: '0',
    currency: CURRENCIES.ron,
    priceIncludesVat: false,
    startDate: today(now),
    months: String(DEFAULT_MONTHS),
  };
}
